import { ReactElement, useMemo } from 'react'

import type { ItemType } from 'antd/es/menu/interface'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { AppTypography } from 'components/AppTypography/AppTypography'
import { Flex, Icon } from 'components/core'
import { ToolTip } from 'components/ToolTip'
import {
    changeCurrencyCode,
    setHiddenAutomationCapabilities,
} from 'features/App/appService'
import {
    changeOrganization,
    changeOrganizationGroup,
} from 'features/Auth/authService'
import { userHasCustomerServicePermissions } from 'helpers/featurePermissions'
import {
    useAuthDomainValue,
    useCurrentOrganization,
    useCurrentOrganizationGroup,
    useModal,
    useUserHasPermissions,
} from 'hooks'
import {
    selectAutomationCapabilitiesMap,
    selectCurrencyCode,
    selectHiddenAutomationCapabilities,
    selectIsSidebarCollapsed,
} from 'selectors/ui'
import { RootReduxState } from 'types'

import DarkMenu from './DarkMenu'
import CurrencyModal from './MenuItems/CurrencyModal'
import ImpersonationModal from './MenuItems/ImpersonationModal'
import OrganizationModal from './MenuItems/OrganizationModal'
import * as styles from './styles.module.scss'

const SideMenuFooter = (): ReactElement => {
    const { t } = useTranslation('navigation')
    const isSidebarCollapsed = useSelector((state: RootReduxState) =>
        selectIsSidebarCollapsed(state)
    )
    const currencyCode = useSelector((state: RootReduxState) =>
        selectCurrencyCode(state)
    )
    const automationCapabilities = useSelector(selectAutomationCapabilitiesMap)
    const hiddenAutomationCapabilities = useSelector(
        selectHiddenAutomationCapabilities
    )

    const isImpersonating = useAuthDomainValue('userIsImpersonating')
    const isImpersonationMode =
        useUserHasPermissions(userHasCustomerServicePermissions) &&
        isImpersonating

    const currentOrganization = useCurrentOrganization()
    const currentOrganizationGroup = useCurrentOrganizationGroup()

    const {
        isModalVisible: isCurrencyModalVisible,
        toggleModalVisible: toggleCurrencyModal,
    } = useModal()
    const {
        isModalVisible: isOrgModalVisible,
        toggleModalVisible: toggleOrgModal,
    } = useModal()
    const {
        isModalVisible: isImpersonationModalVisible,
        toggleModalVisible: toggleImpersonationModal,
    } = useModal()

    const handleChangeOrganization = (organizationId: string): void => {
        if (currentOrganization && currentOrganization.id !== organizationId) {
            changeOrganization({ organizationId })
        }
    }

    const handleChangeOrganizationGroup = (
        organizationId: string,
        organizationGroupId: string
    ): void => {
        if (
            currentOrganizationGroup &&
            currentOrganizationGroup.id !== organizationGroupId
        ) {
            changeOrganizationGroup({
                organizationGroupId,
                organizationId,
            })
        }
    }

    const orgTitle = useMemo(
        () => (
            <Flex
                direction="column"
                style={{ lineHeight: '14px' }}
                alignItems="normal"
            >
                <AppTypography
                    variant="body-main"
                    noGutter
                    style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {currentOrganization?.name}
                </AppTypography>
                {currentOrganizationGroup && (
                    <AppTypography variant="body-xsmall" noGutter>
                        {currentOrganizationGroup.name}
                    </AppTypography>
                )}
            </Flex>
        ),
        [currentOrganization?.name, currentOrganizationGroup]
    )

    const menuItems: ItemType[] = []

    if (currencyCode) {
        menuItems.push({
            key: 'currency',
            icon: <Icon name="NAV_CURRENCY" size={18} />,
            label: !isSidebarCollapsed ? <span>{currencyCode}</span> : null,
            onClick: toggleCurrencyModal,
            title: isSidebarCollapsed
                ? t('navigation:SideMenu.currency', 'Change Currency')
                : undefined,
        })
    }

    if (currentOrganization) {
        const fullOrgText = `${currentOrganization.name}${currentOrganizationGroup ? ` | ${currentOrganizationGroup.name}` : ''}`

        menuItems.push({
            key: 'organization',
            icon: <Icon name="NAV_ORG" size={18} />,
            label: !isSidebarCollapsed ? (
                <ToolTip title={fullOrgText} placement="right">
                    <div>{orgTitle}</div>
                </ToolTip>
            ) : null,
            onClick: isImpersonationMode
                ? toggleImpersonationModal
                : toggleOrgModal,
            title: isSidebarCollapsed ? fullOrgText : undefined,
        })
    }

    return (
        <>
            <DarkMenu
                className={classNames(
                    styles.forgeSideMenu,
                    styles.forgeSideMenuFooter,
                    styles.bottomMenu
                )}
                mode="inline"
                selectable={false}
                items={menuItems}
            />
            {currencyCode && (
                <CurrencyModal
                    modalVisible={isCurrencyModalVisible}
                    closeModal={toggleCurrencyModal}
                    handleChangeCurrencyCode={(nextCurrencyCode) => {
                        if (nextCurrencyCode) {
                            changeCurrencyCode(nextCurrencyCode).catch(() => {})
                        }
                    }}
                    currencyCode={currencyCode}
                />
            )}
            {currentOrganization && !isImpersonationMode && (
                <OrganizationModal
                    modalVisible={isOrgModalVisible}
                    closeModal={toggleOrgModal}
                    currentOrganization={currentOrganization}
                    handleChangeOrganization={handleChangeOrganization}
                />
            )}
            {currentOrganization &&
                currentOrganizationGroup &&
                isImpersonationMode && (
                    <ImpersonationModal
                        modalVisible={isImpersonationModalVisible}
                        closeModal={toggleImpersonationModal}
                        currentOrganization={currentOrganization}
                        currentOrganizationGroup={currentOrganizationGroup}
                        automationCapabilities={automationCapabilities}
                        hiddenAutomationCapabilities={
                            hiddenAutomationCapabilities
                        }
                        handleChangeOrganizationGroup={
                            handleChangeOrganizationGroup
                        }
                        handleChangeHiddenAutomationCapabilities={(
                            capabilities
                        ) => {
                            setHiddenAutomationCapabilities(capabilities).catch(
                                () => {}
                            )
                        }}
                    />
                )}
        </>
    )
}

export default SideMenuFooter
